import React, { useState } from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { priorityStyles, categoryStyles } from './noticePriorityStyles';
import type { Notice } from '../../../types/notice.types';

const MotionBox = motion(Box);

interface NoticeCardProps {
  notice: Notice;
  index: number;
  defaultExpanded?: boolean;
}

/**
 * 개별 공지 카드 컴포넌트
 * 클릭 시 아코디언 확장으로 전체 본문 표시
 */
const NoticeCard: React.FC<NoticeCardProps> = ({ notice, index, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const pStyle = priorityStyles[notice.priority];
  const cStyle = categoryStyles[notice.category];

  const createdDate = (() => {
    try {
      const d = (notice.created_at as any)?.toDate
        ? (notice.created_at as any).toDate()
        : new Date(notice.created_at as any);
      return d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
    } catch {
      return '';
    }
  })();

  // body에서 텍스트만 추출 (HTML 태그 제거)
  const plainText = notice.body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          bgcolor: '#fff',
          borderRadius: 2.5,
          border: '1px solid #f0f0f0',
          borderLeft: `4px solid ${pStyle.borderColor}`,
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#e0e0e0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        {/* 헤더 */}
        <Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 1.5, md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Chip
                label={pStyle.label}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 800,
                  bgcolor: pStyle.bgColor,
                  color: pStyle.color,
                  border: `1px solid ${pStyle.borderColor}30`,
                }}
              />
              <Chip
                label={`${cStyle.icon} ${notice.category}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: 10, fontWeight: 600, borderColor: '#e2e8f0' }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{createdDate}</Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: '#94a3b8',
                  transition: 'transform 0.3s ease',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
          </Box>

          <Typography
            fontWeight={700}
            sx={{
              fontSize: { xs: 14, md: 15 },
              color: '#0f172a',
              mb: expanded ? 0 : 0.5,
            }}
          >
            {notice.title}
          </Typography>

          {/* 접힌 상태: 본문 미리보기 */}
          {!expanded && plainText && (
            <Typography
              sx={{
                fontSize: 13,
                color: '#64748b',
                lineHeight: 1.6,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {plainText}
            </Typography>
          )}
        </Box>

        {/* 확장 영역 */}
        <AnimatePresence>
          {expanded && (
            <MotionBox
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              sx={{ overflow: 'hidden' }}
            >
              <Box sx={{ px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 }, borderTop: '1px dashed #f0f0f0', pt: 2 }}>
                {/* HTML 본문 */}
                <Box
                  sx={{
                    fontSize: 14,
                    color: '#334155',
                    lineHeight: 1.8,
                    '& p': { m: 0, mb: 1 },
                    '& strong': { fontWeight: 700 },
                    '& a': { color: '#2563eb', textDecoration: 'underline' },
                    '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
                    '& ul, & ol': { pl: 2.5 },
                  }}
                  dangerouslySetInnerHTML={{ __html: notice.body }}
                />

                {/* 이미지 */}
                {notice.imageUrl && (
                  <Box
                    component="img"
                    src={notice.imageUrl}
                    alt={notice.title}
                    sx={{
                      width: '100%',
                      maxHeight: 300,
                      objectFit: 'cover',
                      borderRadius: 2,
                      mt: 1.5,
                    }}
                  />
                )}

                {/* CTA 버튼 */}
                {notice.linkUrl && (
                  <Button
                    variant="contained"
                    href={notice.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 13,
                      bgcolor: pStyle.color,
                      '&:hover': { bgcolor: pStyle.color, opacity: 0.9 },
                    }}
                  >
                    {notice.linkLabel || '자세히 보기'}
                  </Button>
                )}
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </Box>
    </MotionBox>
  );
};

export default NoticeCard;
